import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type {
  Exercise,
  Plan,
  PlanSeed,
  PlanStarter,
  ProgramModel,
  Session,
  UnitsPreference,
} from "@heraion/shared";
import { deleteSessionsInOrder, useWorkoutsDiary, workoutReader, writeError, type UiTheme } from "@heraion/ui";

import { apiFetch } from "./api";
import { confirmViaWindow, notifyViaWindow } from "./confirm";
import { durableWrite } from "./durableWrite";
import { fetchSessionDetail } from "./useSessionLoader";
import { useWorkoutStarter } from "./useWorkoutStarter";

const readWorkout = workoutReader(fetchSessionDetail);

// #368: the web's mount of the diary (useWorkoutsDiary). The wide console
// gets the Select button beside List | Calendar; a hold works on both
// widths. The web deletes too now, from the selection bar. Plan, a plan of
// the calendar and «Create program» open the Home surface's editors through
// the shell (AppShell's requestComposer); a started plan opens the logger
// this screen holds.
export function useWebDiary({
  theme,
  wide,
  items,
  unit,
  catalog,
  bodyweightKg,
  resolveName,
  onRefresh,
  onDeleted,
  onStarter,
  onPlanSession,
  onOpenPlan,
  onPlanFromModel,
}: {
  theme: UiTheme;
  wide: boolean;
  items: Session[] | null;
  unit: UnitsPreference;
  catalog: Exercise[] | null;
  bodyweightKg: number | null;
  resolveName: (slug: string, snapshot?: string) => string;
  onRefresh: () => void;
  /** The ids that are gone: the detail pane lets go of one of them. */
  onDeleted: (ids: ReadonlySet<string>) => void;
  onStarter: (starter: PlanStarter) => void;
  onPlanSession?: (seed: PlanSeed) => void;
  onOpenPlan?: (plan: Plan) => void;
  onPlanFromModel?: (model: Omit<ProgramModel, "id">) => void;
}) {
  const { t } = useTranslation();
  const { starting, startPlan } = useWorkoutStarter(catalog, bodyweightKg, onStarter);
  // The diary says when some were not deleted; the reason goes to the log.
  const deleteSessions = useCallback(
    async (ids: readonly string[]) => {
      const { gone, refused } = await deleteSessionsInOrder(durableWrite, ids);
      if (refused) console.warn("[workouts] delete_failed", writeError(refused)?.message);
      if (gone.length > 0) {
        onDeleted(new Set(gone));
        onRefresh();
      }
      return gone;
    },
    [onDeleted, onRefresh],
  );
  return useWorkoutsDiary({
    theme,
    items,
    showSelectButton: wide,
    dockedBar: false,
    apiFetch,
    durableWrite,
    confirm: confirmViaWindow,
    readWorkout,
    deleteSessions,
    onPlanSeed: (seed) => onPlanSession?.(seed),
    onPlanFromModel: (model) => onPlanFromModel?.(model),
    onOpenPlan: (plan) => onOpenPlan?.(plan),
    onStartPlan: (plan) => void startPlan(plan),
    startingPlanId: starting,
    unit,
    catalog,
    resolveName,
    onError: (message) => notifyViaWindow(t("errors.generic"), message),
  });
}
